/**
 * Look-alike ShipBob inventory items.
 *
 * The catalog carries several items that read as the same product: an old
 * listing and its relabeled successor, the same protector made in China and in
 * Vietnam, two items sharing one Danican product number. A PO line pointing at
 * the wrong one restocks the wrong ShipBob item, so before a PO is saved,
 * printed, emailed, or pushed, lines with look-alikes are confirmed by a
 * person. Nothing here looks at stock or velocity on purpose (Inno): the item
 * being restocked may have been out of stock for months while its twin sold.
 */

export type InventoryOption = { id: string; name: string | null; skus: string[] };

export type LookAlike = {
  option: InventoryOption;
  /** Why it looks like the line's item */
  reasons: string[];
};

const NAME_NOISE = new Set(["labeled", "us", "ca", "au", "uk", "aus", "can", "the", "a"]);

/** "King Cooling Mattress Protector-LABELED" and "King Cooling Mattress Protector (US)" compare equal */
export function normalizeName(name: string | null | undefined): string {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(token => token && !NAME_NOISE.has(token))
    .sort()
    .join(" ");
}

/**
 * The SKU without its trailing factory + origin codes:
 * DZ-US-MP-TWN-WL-CH → DZ-US-MP-TWN, so the WL-CH and WL-VN items share a stem.
 * Only SKUs in the house convention (≥4 dash-separated parts, two 2-letter tails) get a stem.
 */
export function skuStem(sku: string): string | null {
  const parts = sku.toUpperCase().split("-");
  if (parts.length < 4) return null;
  const tail = parts.slice(-2);
  if (!tail.every(part => /^[A-Z]{2}$/.test(part))) return null;
  return parts.slice(0, -2).join("-");
}

/** Items that could be mistaken for `inventoryId`, with the reason for each */
export function findLookAlikes(
  inventoryId: string,
  options: InventoryOption[],
  manufacturerSkus: Record<string, string> | undefined,
): LookAlike[] {
  const self = options.find(opt => opt.id === inventoryId);
  if (!self) return [];
  const selfName = normalizeName(self.name);
  const selfStems = new Set(self.skus.map(skuStem).filter((stem): stem is string => Boolean(stem)));
  const selfMfr = manufacturerSkus?.[inventoryId]?.trim().toUpperCase() || null;

  const found: LookAlike[] = [];
  for (const opt of options) {
    if (opt.id === inventoryId) continue;
    const reasons: string[] = [];
    const mfr = manufacturerSkus?.[opt.id]?.trim().toUpperCase() || null;
    if (selfMfr && mfr === selfMfr) reasons.push(`same Danican # ${selfMfr}`);
    if (selfName && normalizeName(opt.name) === selfName) reasons.push("same name");
    const sharedStem = opt.skus.map(skuStem).find(stem => stem && selfStems.has(stem));
    if (sharedStem) reasons.push(`same SKU family ${sharedStem}`);
    if (reasons.length > 0) found.push({ option: opt, reasons });
  }
  return found;
}
